/** PointFive Score screen model — the reference pfModel() ported verbatim (design-reference script lines 1637–1766).
 *  Composite card, pillar band, "what moved your score", investors, public card and leaderboard opt-in; every number comes
 *  from c.pfEngine() (the dashboard dial's engine stays authoritative) and c.data.score.
 *  Omitted: `pfPublicOpen` (returned by the reference, never bound by the template — absent from the generated ViewModel). */
import type { Ctx } from './ctx';
import type { PfPillarOut } from './pfEngine';
import type { ViewModel } from '@/screens/generated/view-model';

export function buildScore(c: Ctx) {
  const S = c.S;
  const D = c.data.score;
  const E = c.pfEngine(); // the dashboard dial's engine stays authoritative
  const score = E.final_score;
  const conf = Math.round(E.confidence * 100);
  // Owner directive 2026-08-27 §8. A calibrated account renders exactly as it did before — the demo trader has
  // 2,184 eligible trades, so every reference pixel is untouched — and only a provisional one reads differently.
  // That is deliberate: the directive changes what a NEW account is told, and a change that also moved the
  // reference render would have been a redesign wearing a bug fix's clothes.
  const provisional = E.calibration_state === 'provisional';
  const TIERS = D.tiers; // [[min, name], …] high → low
  const rawIdx = TIERS.findIndex(([min]) => score >= min);
  const dispName = E.displayed_tier;
  const dispIdx = TIERS.findIndex(([, n]) => n === dispName);
  const tierNo = TIERS.length - dispIdx; // Tier 1 of 6 = lowest
  const GRADE: Record<string, string> = {
    Exceptional: 'A+',
    Elite: 'A',
    Professional: 'B+',
    Competent: 'B',
    Developing: 'C',
    Novice: 'D',
  };
  const topPct = Math.max(1, 100 - E.percentile); // derived from the engine's own percentile
  const pctLabel = E.percentile >= 50 ? 'Top ' + topPct + '%' : 'Bottom ' + E.percentile + '%';
  const TONE: Record<string, string> = {
    Exceptional: 'var(--amb)',
    Elite: 'var(--vio-fill)',
    Professional: 'var(--pos)',
    Competent: 'var(--sky-fill)',
    Developing: 'var(--amb)',
    Novice: 'var(--neg)',
  };
  const INK: Record<string, string> = {
    Exceptional: 'var(--amb-2)',
    Elite: 'var(--vio)',
    Professional: 'var(--pos-2)',
    Competent: 'var(--sky)',
    Developing: 'var(--amb-2)',
    Novice: 'var(--neg)',
  };
  const tone = TONE[dispName],
    ink = INK[dispName];
  const grade = GRADE[dispName];
  const nextIdx = rawIdx - 1;
  const next = nextIdx >= 0 ? TIERS[nextIdx] : null;
  const nextMin = next ? next[0] : 1000;
  const nextName = next ? next[1] : dispName;

  // pillar row, ordered and coloured as the spec card
  const ORDER = ['bi', 'rm', 'rar', 'cr', 'om'];
  const LABEL: Record<string, string> = {
    bi: 'Behavioral',
    rm: 'Risk',
    rar: 'Returns',
    cr: 'Consistency',
    om: 'Operational',
  };
  const BAR: Record<string, string> = {
    bi: 'var(--neg)',
    rm: 'var(--vio-fill)',
    rar: 'var(--pos)',
    cr: 'var(--sky-fill)',
    om: 'var(--amb)',
  };
  const byId: Record<string, PfPillarOut> = {};
  E.pillars.forEach((p) => {
    byId[p.id] = p;
  });
  const pillarRow = ORDER.flatMap((id) => {
    const p = byId[id];
    if (!p) return []; // PF_SPEC carries all five ids; guard only narrows the type
    return [
      {
        label: LABEL[id],
        score: String(p.points),
        max: '/ ' + p.max,
        w: ((p.points / p.max) * 100).toFixed(1) + '%',
        bar: BAR[id],
        metrics: p.metrics.map((m) => m.name).join(' · '),
      },
    ];
  });

  // what moved the score — strongest and weakest pillar against its own weight
  const ranked = E.pillars.slice().sort((a, b) => b.points / b.max - a.points / a.max);
  const best = ranked[0]!,
    worst = ranked[ranked.length - 1]!; // PF_SPEC guarantees five pillars
  const impactOf = (p: PfPillarOut) => Math.round(p.points - p.max * 0.5);

  // weekly snapshot series
  const HIST = E.snapshot_labels.map((_, i) => {
    const frac = (i + 1) / E.snapshot_labels.length;
    return Math.round(score * (0.42 + 0.58 * Math.min(1, frac * 1.35)));
  });
  HIST[HIST.length - 1] = score;
  const hLo = Math.min(...HIST),
    hHi = Math.max(...HIST);
  const hSpan = Math.max(1, hHi - hLo);
  const pts = HIST.map(
    (v, i) =>
      ((i / Math.max(1, HIST.length - 1)) * 560).toFixed(1) +
      ',' +
      (96 - ((v - hLo) / hSpan) * 78).toFixed(1),
  );
  const prevWeek = HIST[HIST.length - 2] ?? NaN; // one-snapshot ranges yield NaN exactly as the reference does
  const weekDelta = score - prevWeek;

  const INVESTORS = D.investors;
  const rank = '#' + Math.max(1, Math.round(((100 - E.percentile) / 100) * D.leaderboard.population));
  const rankOf = '/ ' + D.leaderboard.population.toLocaleString('en-US');
  const boardRows = [
    { crown: true, name: 'You', you: true, score: String(score), tier: dispName.toUpperCase(), delta: '+0' },
  ];

  return {
    pfSubtitle:
      E.pillars.length + ' pillars · ' + E.pillars.reduce((a, p) => a + p.metrics.length, 0) + ' metrics',
    pfPreview: () => {
      c.set((s) => ({ pfPublic: !s.pfPublic }));
      c.flash(S.pfPublic ? 'Closed the public card' : 'Previewing your public card', 'pos');
    },
    pfPreviewLabel: S.pfPublic ? 'Hide public card' : 'Preview public card',
    pfShareInvestor: () => c.flash('Share link created — investors can now read this card', 'pos'),
    // ── composite card ──
    pfMeta: 'Composite · ' + E.score_version + ' · ' + E.trades.toLocaleString('en-US') + ' trades',
    pfScore: String(score),
    pfGrade: grade,
    pfTierLine: '/ 1000 · Tier ' + tierNo + ' of ' + TIERS.length,
    pfTone: tone,
    pfInk: ink,
    pfProgLabel: 'Progress to ' + nextName,
    pfProgRatio: score + ' / ' + nextMin,
    // A scaleX factor, not a width: the meter is a full-width bar the browser scales on the compositor, so the
    // growth costs no layout. Multiplied by the entrance ramp so it grows from zero on first paint.
    pfProgScale: ((Math.min(100, (score / nextMin) * 100) / 100) * (c.S.intro ?? 1)).toFixed(4),
    pfWeekDelta: (weekDelta >= 0 ? '+' : '−') + Math.abs(weekDelta) + ' this week',
    pfWeekDeltaFg: weekDelta >= 0 ? 'var(--pos)' : 'var(--neg)',
    pfSnapCadence: 'Score · ' + E.resolution + ' snapshots',
    pfSnapCount: HIST.length + ' ' + E.resolution + ' snapshots on record',
    pfRank: rank,
    pfRankOf: rankOf,
    pfTierChip: dispName + ' · ' + pctLabel,
    // "Calibrating — N/500 trades" is the directive's own wording, and it replaces a percentage that could only
    // ever have read 0% for a new account. A count is something a trader can act on; "0% confidence" told them
    // their evidence was worthless when what it meant was that there was not yet enough of it.
    pfConfPct: provisional
      ? 'Calibrating — ' + E.eligible_trades + '/' + E.calibration_threshold + ' trades'
      : conf + '%',
    // The note shows for either reason now: a reduction, or a provisional score explaining itself. Before the
    // directive a provisional account was reduced, so the reduction WAS the explanation; now that it is not
    // reduced, the note is the only place the state is stated.
    pfTierNote: E.tier_reduced_by > 0 || provisional,
    pfRawTier: TIERS[rawIdx]?.[1],
    pfDispTier: dispName,
    pfConfNote: provisional
      ? 'Provisional · calibrating on ' + E.eligible_trades + ' of ' + E.calibration_threshold + ' trades'
      : 'due to low confidence (' + conf + '%)',
    pfSnapPath: 'M' + pts.join(' L'),
    pfSnapFill: 'M' + pts.join(' L') + ' L560,96 L0,96 Z',
    pfSnapLabels: E.snapshot_labels
      .filter((_, i) => i % Math.max(1, Math.ceil(E.snapshot_labels.length / 8)) === 0)
      .map((l) => ({ l })),
    // ── pillar band ──
    pfPillarHead:
      E.pillars.length +
      ' pillars · ' +
      E.pillar_total +
      ' base ' +
      // v1.4's modifiers are conditional (§10), so an unmodified score is now a real outcome rather than an
      // impossible one — "+ +0" would have been the reference format's answer to a case it never had to render.
      (E.modifier === 0 ? '± ' : E.modifier > 0 ? '+ +' : '− ') +
      Math.abs(E.modifier) +
      ' cross-pillar modifier = ' +
      score,
    pfPillarRow: pillarRow,
    // ── what moved your score ──
    pfMoved: [
      {
        title:
          LABEL[worst.id] === 'Operational'
            ? 'Operational Maturity is your weakest pillar'
            : worst.name + ' is your weakest pillar',
        sub: worst.metrics.map((m) => m.name).join(' · '),
        v: (impactOf(worst) >= 0 ? '+' : '−') + Math.abs(impactOf(worst)),
        fg: impactOf(worst) >= 0 ? 'var(--pos)' : 'var(--neg)',
      },
      {
        title: best.name + ' is your strongest pillar',
        sub: best.metrics.map((m) => m.name).join(' · '),
        v: (impactOf(best) >= 0 ? '+' : '−') + Math.abs(impactOf(best)),
        fg: impactOf(best) >= 0 ? 'var(--pos)' : 'var(--neg)',
      },
    ],
    pfConfRingFg: conf >= 80 ? 'var(--pos)' : conf >= 50 ? 'var(--amb-2)' : 'var(--neg)',
    pfConfRingBg:
      'color-mix(in srgb, ' +
      (conf >= 80 ? 'var(--pos)' : conf >= 50 ? 'var(--amb)' : 'var(--neg)') +
      ' 14%, transparent)',
    pfConfSub: E.trades.toLocaleString('en-US') + ' trades · ' + E.track_record_months.toFixed(1) + ' months',
    pfPnl: '+$' + Math.round(E.trades * 137).toLocaleString('en-US') + ' P&L',
    pfWr: E.win_rate.replace('.0', '').replace(/\.\d/, '') + ' WR',
    // ── investors ──
    pfInvestorHead: '3 active interests · 2 capital offers',
    pfInvestors: INVESTORS,
    pfPrivacy: () => c.flash('Privacy settings — choose which pillars and accounts to publish', 'mut'),
    pfMessageAll: () => c.flash('Message drafted to all 3 investors', 'pos'),
    pfReview: () => c.flash('Offer opened for review', 'pos'),
    // ── public card ──
    pfTraderName: D.profile.name,
    pfVerified: 'Verified by PointFive · ' + E.as_of,
    pfPublicSub:
      HIST.length +
      ' ' +
      E.resolution +
      ' snapshots · ' +
      E.trades.toLocaleString('en-US') +
      ' verified trades · ' +
      pctLabel +
      ' globally.',
    pfPublicPnl: '+$' + Math.round(E.trades * 137).toLocaleString('en-US'),
    pfPublicWr: E.win_rate,
    pfPublicWrSub: E.trades.toLocaleString('en-US') + ' trades',
    pfPublicGrade: grade + ' · ' + pctLabel,
    pfPublicChips: [{ label: dispName }, { label: rank + ' ' + rankOf }],
    pfPublicSlug: D.profile.slug,
    pfBoardRows: boardRows,
    pfOptIn: S.pfOptIn,
    pfOptInToggle: () => {
      c.set((s) => ({ pfOptIn: !s.pfOptIn }));
      c.flash(
        S.pfOptIn ? 'Removed from the leaderboard' : 'You will appear on the leaderboard',
        S.pfOptIn ? 'mut' : 'pos',
      );
    },
    pfOptTrack: S.pfOptIn ? 'color-mix(in srgb, var(--pos) 55%, transparent)' : 'var(--l10)',
    pfOptKnob: S.pfOptIn ? 'translateX(14px)' : 'translateX(0)',
    pfOptKnobBg: S.pfOptIn ? 'var(--pos)' : 'var(--muted2)',
  } satisfies Partial<ViewModel>;
}
export type ScoreModel = ReturnType<typeof buildScore>;
